// <pfs-notification-inbox>: generisches Postfach-WC (PFS-688 P4).
//
// EIN Component, ZWEI Oberflaechen mit eigener Optik UND eigenem Inhalt (Pascal
// 2026-07-12):
//   • Icon-Card (Bell-Dropdown, kompakt, data-summary/view-all): eine GROBE
//     UEBERSICHT (N ungelesen + Kategorie-Breakdown) + ein „Zum Postfach"-Link.
//     KEINE Item-Liste. Optik 1:1 <pfs-system-status> (min-width, Uppercase-
//     Header, Spin-Refresh). Fetcht nur den unread-count-Endpoint.
//   • Content-Card (voller /user/inbox-Tab): die volle Item-Liste in der ueberall
//     herrschenden Content-Optik (<pfs-card> + <pfs-card-header> +
//     <pfs-loading-indicator> + <pfs-empty-state>). Fetcht die Notifications.
//
// URL-Konvention: BARE relative Pfade — apiFetch prependet den Host-Mount-Prefix
// zentral zur Fetch-Zeit (withApiBase, EIN Mechanismus, kein Freeze).
//
// I18N: alle sichtbaren Strings sind DATA-GETRIEBEN (englischer Default, der
// Consumer/Tenant liefert seine Sprache) — wie ein generisches WC lokalisiert.
// Kategorie-Labels folgen server-seitig via pfs-content (hier Fallback = Key).
//
// Attribute (alle data-*):
//   data-source-url · data-limit · data-view-all-href · data-summary
//   data-title · data-subtitle · data-empty-title · data-empty-message
//   data-view-all-label · data-unread-label · data-mark-all-label ·
//   data-mark-read-label · data-error-label   (sichtbare Strings, i18n)
//   data-locale · data-time-zone · data-events-url · data-event-name

import DOMPurify from "dompurify";
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { apiFetch } from "../../store/api-fetch.js";
import { formatDateTime } from "../../utils/format-datetime.js";
import { escapeHTML } from "../../utils/escape-html.js";
import { liveBus } from "../../store/live-bus.js";
import { NOTIFICATION_PUSHED_EVENT } from "../../store/event-key.js";
import { confirmDialog } from "../confirm-dialog/pfs-confirm-dialog.js";
import "../layout/pfs-card.js";
import "../card-body/pfs-card-body.js";
import "../card-header/pfs-card-header.js";
import "../empty-state/pfs-empty-state.js";
import "../loading-indicator/pfs-loading-indicator.js";
import "../card-mini/pfs-card-mini.js";

const DEFAULT_SOURCE = "/api/v1/me/notifications";
const DEFAULT_LIMIT = 25;

interface RenderedPart {
  value?: string;
  sanitize_hint?: string;
  variant?: string;
}
type Rendered = Record<string, RenderedPart | undefined>;

interface InboxItem {
  deliveryId: string;
  eventKey: string;
  category: string;
  rendered: Rendered;
  createdAt: string;
  readAt?: string;
}

// list.Page of the framework canon: capitalised field names. The former
// lowercase shape is NOT accepted (PFS-1504, no silent fallback).
interface ListBody {
  Items: InboxItem[];
  Total: number;
  Limit: number;
  Offset: number;
}

const STYLES = `
${SHADOW_RESET_CSS}
${BUTTON_CSS}
:host { display: block; }

/* ── ICON-CARD (Dropdown) = Übersicht + Link ──────────────────────────────
   Card-Shell (Rahmen/Header/Titel/Body + loading) kommt aus <pfs-card-mini>
   (PFS-705). Hier bleibt nur das Inbox-Spezifische: Refresh-Button (actions-
   Slot), summary/empty/error/link (default-Slot). Der Link ist IMMER sichtbar
   (auch bei 0 ungelesen) → die Inbox managt summary/empty/error selbst und
   nutzt data-state nur für loading. */
:host([data-compact]) { min-width: 18rem; }
.icon-card__refresh { border: 0; background: transparent; cursor: pointer; padding: 0; display: inline-flex; align-items: center; justify-content: center; color: var(--pfs-color-on-surface-subtle); transition: color 150ms ease; }
.icon-card__refresh:hover { color: var(--pfs-color-on-surface-muted); }
.icon-card__refresh:disabled { cursor: wait; }
.icon-card__refresh.is-loading svg { animation: pfs-inbox-spin 1s linear infinite; }
@keyframes pfs-inbox-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.icon-card__empty { font-size: 0.8rem; color: var(--pfs-color-on-surface-subtle); }
.icon-card__error { font-size: 0.8rem; color: var(--pfs-color-danger); }
.summary-head { font-weight: 600; font-size: 0.85rem; color: var(--pfs-color-on-surface); }
.summary-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.4rem; }
.summary-chip { font-size: 0.72rem; padding: 0.12rem 0.5rem; border-radius: 9999px; background: var(--pfs-color-surface); border: 1px solid var(--pfs-color-outline); color: var(--pfs-color-on-surface-muted); }
/* „Zum Postfach" = echter Button (BUTTON_CSS .btn.secondary.sm) statt Text-Link
   — konsistent zu allen Aktionen (Tab + Mini-Card gleich). Bleibt ein <a>
   (Navigation), nur button-optisch; text-decoration:none, damit kein Link-Unterstrich. */
.icon-card__link { text-decoration: none; }
.icon-card__link[hidden] { display: none; }

/* ── CONTENT-CARD (Tab) = volle Liste im pfs-card ─────────────────────────── */
.content-list-wrap { display: flex; flex-direction: column; gap: 0.75rem; }
.list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.75rem; }
.list[hidden] { display: none; }
.item { display: flex; flex-direction: column; border: 1px solid var(--pfs-color-outline); border-radius: var(--pfs-radius-md, 0.5rem); padding: 1rem; background: var(--pfs-color-surface-sunken); }
.item.unread { border-left: 3px solid var(--pfs-color-primary); }
.item-title { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; font-size: 0.95rem; color: var(--pfs-color-on-surface); word-break: break-word; }
.dot { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--pfs-color-primary); flex-shrink: 0; }
.time { color: var(--pfs-color-on-surface-subtle); font-size: 0.8rem; margin-top: 0.2rem; font-variant-numeric: tabular-nums; }
.body { margin-top: 0.75rem; padding: 0.75rem; border-radius: var(--pfs-radius-md, 0.5rem); border: 1px solid var(--pfs-color-outline); background: var(--pfs-color-surface); font-size: 0.85rem; color: var(--pfs-color-on-surface); word-break: break-word; }
.body:empty { display: none; }
.body.info { background: var(--pfs-color-info-container); border-color: var(--pfs-color-info-container-border); color: var(--pfs-color-on-info-container); }
.body.warn { background: var(--pfs-color-warning-container); border-color: var(--pfs-color-warning-container-border); color: var(--pfs-color-on-warning-container); }
.body.danger { background: var(--pfs-color-danger-container); border-color: var(--pfs-color-danger-container-border); color: var(--pfs-color-on-danger-container); }
.body.muted { opacity: 0.65; }
/* Aktionen = echte Buttons (BUTTON_CSS .btn.secondary.sm), nicht Text-Links —
   folgt der plattformweiten Button-Konvention (PFS-688 P4-Nachbesserung). */
/* Item-Aktionsreihe (rechtsbündig): Spacing per Token statt Inline-Style. */
.item-actions { display: flex; gap: var(--pfs-space-action, 0.5rem); justify-content: flex-end; margin-top: var(--pfs-space-action, 0.5rem); }
.markall[hidden] { display: none; }
.content-error { font-size: 0.875rem; color: var(--pfs-color-danger); padding: 0.75rem 0; }
`;

const BODY_VARIANTS = new Set(["info", "warn", "danger", "muted"]);

function renderPart(part: RenderedPart | undefined): string {
  const value = part?.value ?? "";
  if (!value) return "";
  return part?.sanitize_hint ? DOMPurify.sanitize(value) : escapeHTML(value);
}

const REFRESH_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182"/></svg>`;
// Default-Header-Icon der Content-Card (Postfach), slotbar ins <pfs-card-header>.
const ICON_INBOX = '<svg slot="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>';

interface Labels {
  viewAll: string;
  unread: string;
  markAll: string;
  markRead: string;
  deleteOne: string;
  deleteAll: string;
  cancel: string;
  deleteConfirm: string;
  deleteAllConfirm: string;
  error: string;
  loading: string;
}

defineComponent({
  tagName: "pfs-notification-inbox",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;

    const rawSource = host.getAttribute("data-source-url")?.trim() || DEFAULT_SOURCE;
    const limit = parseInt(host.getAttribute("data-limit") ?? "", 10) || DEFAULT_LIMIT;
    const viewAllHref = host.getAttribute("data-view-all-href")?.trim() || "";
    const headerTitle = host.getAttribute("data-title")?.trim() || "Notifications";
    const emptyTitle = host.getAttribute("data-empty-title")?.trim() || "No notifications";
    const emptyMessage = host.getAttribute("data-empty-message")?.trim() || "";
    const locale = host.getAttribute("data-locale")?.trim() || undefined;
    const timeZone = host.getAttribute("data-time-zone")?.trim() || undefined;
    const eventsUrl = host.getAttribute("data-events-url")?.trim() || "";
    const eventName = host.getAttribute("data-event-name")?.trim() || NOTIFICATION_PUSHED_EVENT;
    const countUrl = `${rawSource}/unread-count`;

    // Sichtbare Strings data-getrieben (i18n): englischer Default, Consumer setzt Sprache.
    const L: Labels = {
      viewAll: host.getAttribute("data-view-all-label")?.trim() || "View all",
      unread: host.getAttribute("data-unread-label")?.trim() || "unread",
      markAll: host.getAttribute("data-mark-all-label")?.trim() || "Mark all read",
      markRead: host.getAttribute("data-mark-read-label")?.trim() || "Mark read",
      deleteOne: host.getAttribute("data-delete-label")?.trim() || "Delete",
      deleteAll: host.getAttribute("data-delete-all-label")?.trim() || "Clear all",
      cancel: host.getAttribute("data-cancel-label")?.trim() || "Cancel",
      deleteConfirm: host.getAttribute("data-delete-confirm-label")?.trim() || "Delete this notification?",
      deleteAllConfirm:
        host.getAttribute("data-delete-all-confirm-label")?.trim() || "Delete all notifications?",
      error: host.getAttribute("data-error-label")?.trim() || "Could not load notifications.",
      loading: host.getAttribute("data-loading-label")?.trim() || "Loading",
    };

    // Dropdown (data-summary/view-all) = Icon-Card (Übersicht+Link); sonst Tab = Content-Card (Liste).
    const compact = host.hasAttribute("data-summary") || !!viewAllHref;
    if (compact) host.setAttribute("data-compact", "");

    const style = document.createElement("style");
    style.textContent = STYLES;

    const notifyChanged = (): void => {
      document.body.dispatchEvent(new CustomEvent("pfs:notifications-changed", { bubbles: false }));
    };
    const markRead = async (deliveryId: string): Promise<void> => {
      try {
        await apiFetch(`${rawSource}/${encodeURIComponent(deliveryId)}/read`, { method: "POST" });
      } finally {
        notifyChanged();
      }
    };
    // 🅑: Aus dem eigenen Postfach entfernen (soft). Backend emittiert pfs-notifications.delivery.soft_deleted.
    const deleteOne = async (deliveryId: string): Promise<void> => {
      try {
        await apiFetch(`${rawSource}/${encodeURIComponent(deliveryId)}/delete`, { method: "POST" });
      } finally {
        notifyChanged();
      }
    };
    const humanizeCategory = (c: string): string => (c ? c.charAt(0).toUpperCase() + c.slice(1) : c);

    const load: () => Promise<void> = compact ? buildIconCard() : buildContentCard();

    const changedTarget = document.body;
    const onChanged = (): void => void load();
    changedTarget.addEventListener("pfs:notifications-changed", onChanged);
    ctx.cleanup.push(() => changedTarget.removeEventListener("pfs:notifications-changed", onChanged));

    void load();

    if (typeof IntersectionObserver !== "undefined") {
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) void load();
      });
      io.observe(host);
      ctx.cleanup.push(() => io.disconnect());
    }

    if (eventsUrl) {
      ctx.cleanup.push(
        liveBus.track({
          url: eventsUrl,
          eventName,
          key: `pfs-notification-inbox:${eventName}:${rawSource}`,
          onValue: () => void load(),
        }),
      );
    }

    // ── ICON-CARD (Dropdown) = grobe Übersicht + „Zum Postfach"-Link ───────
    // Geteilte kompakte Card-Shell via <pfs-card-mini> (PFS-705): Header
    // (Titel + Refresh im actions-Slot) + loading-Zustand. Der Inbox-Inhalt
    // (summary/empty/error + IMMER sichtbarer Link) liegt im default-Slot;
    // data-state nur „loading" (Erst-Fetch) bzw. „ready".
    function buildIconCard(): () => Promise<void> {
      const card = document.createElement("pfs-card-mini");
      card.setAttribute("data-title", headerTitle);
      card.setAttribute("data-loading-label", L.loading);

      const refreshBtn = document.createElement("button");
      refreshBtn.type = "button";
      refreshBtn.slot = "actions";
      refreshBtn.className = "icon-card__refresh";
      refreshBtn.setAttribute("aria-label", "Refresh");
      refreshBtn.title = "Refresh";
      refreshBtn.innerHTML = REFRESH_SVG;

      const errorEl = document.createElement("div");
      errorEl.className = "icon-card__error";
      errorEl.hidden = true;
      const emptyEl = document.createElement("div");
      emptyEl.className = "icon-card__empty";
      emptyEl.hidden = true;
      emptyEl.textContent = emptyTitle;
      const summaryEl = document.createElement("div");
      summaryEl.className = "summary";
      summaryEl.hidden = true;
      const linkEl = document.createElement("a");
      linkEl.className = "icon-card__link btn secondary sm block";
      linkEl.textContent = `${L.viewAll} →`;
      if (viewAllHref) linkEl.href = viewAllHref;
      else linkEl.hidden = true;

      card.append(refreshBtn, errorEl, emptyEl, summaryEl, linkEl);
      root.append(style, card);

      const onRefresh = (): void => void run();
      refreshBtn.addEventListener("click", onRefresh);
      ctx.cleanup.push(() => refreshBtn.removeEventListener("click", onRefresh));

      let hasRendered = false;
      const run = async (): Promise<void> => {
        refreshBtn.classList.add("is-loading");
        refreshBtn.disabled = true;
        errorEl.hidden = true;
        // Erst-Fetch: loading-Shell. Re-Fetch: Inhalt sichtbar lassen (nur Spin).
        if (!hasRendered) card.setAttribute("data-state", "loading");
        try {
          const s = await apiFetch<{ count: number; byCategory: Array<{ category: string; count: number }> }>(countUrl);
          card.setAttribute("data-state", "ready");
          const total = s?.count ?? 0;
          const byCat = s?.byCategory ?? [];
          if (total <= 0) {
            emptyEl.hidden = false;
            summaryEl.hidden = true;
          } else {
            emptyEl.hidden = true;
            summaryEl.hidden = false;
            const head = document.createElement("div");
            head.className = "summary-head";
            head.textContent = `${total} ${L.unread}`;
            const chips = document.createElement("div");
            chips.className = "summary-chips";
            for (const c of byCat) {
              const chip = document.createElement("span");
              chip.className = "summary-chip";
              chip.textContent = `${humanizeCategory(c.category)} ${c.count}`;
              chips.appendChild(chip);
            }
            summaryEl.replaceChildren(head, chips);
          }
          hasRendered = true;
        } catch {
          card.setAttribute("data-state", "ready");
          summaryEl.hidden = true;
          emptyEl.hidden = true;
          errorEl.hidden = false;
          errorEl.textContent = L.error;
        } finally {
          refreshBtn.classList.remove("is-loading");
          refreshBtn.disabled = false;
        }
      };

      return run;
    }

    // ── CONTENT-CARD (Tab) = volle Item-Liste ─────────────────────────────
    function buildContentCard(): () => Promise<void> {
      // Sektionierte Card-SSOT (PFS-761/PFS-760): data-sectioned → Header-Band +
      // Divider + Body-Zone. Die Card erkennt den <pfs-card-header> automatisch.
      const card = document.createElement("pfs-card");
      card.setAttribute("data-sectioned", "");
      const cardHeader = document.createElement("pfs-card-header");
      cardHeader.setAttribute("data-title", headerTitle);
      const subtitle = host.getAttribute("data-subtitle")?.trim();
      if (subtitle) cardHeader.setAttribute("data-subtitle", subtitle);
      // Default-Header-Icon (Postfach) als erstes Kind — insertAdjacentHTML statt
      // innerHTML, damit die actions-Buttons (unten) erhalten bleiben.
      cardHeader.insertAdjacentHTML("afterbegin", ICON_INBOX);
      const markAll = document.createElement("button");
      markAll.type = "button";
      markAll.className = "markall btn secondary sm";
      markAll.slot = "actions";
      markAll.hidden = true;
      markAll.textContent = L.markAll;
      cardHeader.appendChild(markAll);
      const clearAll = document.createElement("button");
      clearAll.type = "button";
      clearAll.className = "clearall btn danger sm";
      clearAll.slot = "actions";
      clearAll.hidden = true;
      clearAll.textContent = L.deleteAll;
      cardHeader.appendChild(clearAll);
      card.appendChild(cardHeader);

      // Body-Zone: loading/error/empty/Liste wohnen sektioniert unter dem Band.
      const body = document.createElement("pfs-card-body");

      const loadingEl = document.createElement("pfs-loading-indicator");
      loadingEl.setAttribute("data-variant", "main");
      loadingEl.setAttribute("data-text", L.loading);
      loadingEl.hidden = true;
      body.appendChild(loadingEl);

      const errorEl = document.createElement("p");
      errorEl.className = "content-error";
      errorEl.hidden = true;
      body.appendChild(errorEl);

      const emptyEl = document.createElement("pfs-empty-state");
      emptyEl.setAttribute("data-variant", "plain");
      emptyEl.setAttribute("data-title", emptyTitle);
      if (emptyMessage) emptyEl.setAttribute("data-message", emptyMessage);
      emptyEl.hidden = true;
      body.appendChild(emptyEl);

      const wrap = document.createElement("div");
      wrap.className = "content-list-wrap";
      const listEl = document.createElement("ul");
      listEl.className = "list";
      wrap.appendChild(listEl);
      body.appendChild(wrap);

      card.appendChild(body);

      const onMarkAll = async (): Promise<void> => {
        try {
          await apiFetch(`${rawSource}/read-all`, { method: "POST" });
        } finally {
          notifyChanged();
        }
      };
      markAll.addEventListener("click", onMarkAll);
      ctx.cleanup.push(() => markAll.removeEventListener("click", onMarkAll));

      const onClearAll = async (): Promise<void> => {
        // Bulk-destruktiv → Bestaetigung (Kanon PFS-742; confirmDialog wie die
        // Admin-Listen: variant danger, cancelLabel "Abbrechen").
        const ok = await confirmDialog({
          title: L.deleteAll,
          message: L.deleteAllConfirm,
          confirmLabel: L.deleteAll,
          cancelLabel: L.cancel,
          variant: "danger",
        });
        if (!ok) return;
        try {
          await apiFetch(`${rawSource}/delete-all`, { method: "POST" });
        } finally {
          notifyChanged();
        }
      };
      clearAll.addEventListener("click", onClearAll);
      ctx.cleanup.push(() => clearAll.removeEventListener("click", onClearAll));

      const paintItem = (item: InboxItem): HTMLLIElement => {
        const li = document.createElement("li");
        const unread = !item.readAt;
        li.className = unread ? "item unread" : "item";

        const titleRow = document.createElement("div");
        titleRow.className = "item-title";
        if (unread) {
          const dot = document.createElement("span");
          dot.className = "dot";
          titleRow.appendChild(dot);
        }
        const titleText = document.createElement("span");
        titleText.innerHTML = renderPart(item.rendered?.title) || escapeHTML(item.eventKey);
        titleRow.appendChild(titleText);

        const timeEl = document.createElement("div");
        timeEl.className = "time";
        timeEl.textContent = formatDateTime(item.createdAt, { locale, timeZone });
        li.append(titleRow, timeEl);

        const bodyHTML = renderPart(item.rendered?.body);
        if (bodyHTML) {
          const bodyEl = document.createElement("div");
          const variant = item.rendered?.body?.variant;
          bodyEl.className = variant && BODY_VARIANTS.has(variant) ? `body ${variant}` : "body";
          bodyEl.innerHTML = bodyHTML;
          li.appendChild(bodyEl);
        }

        // Item-Aktionen rechtsbündig: Mark read (nur ungelesen) + Löschen (immer).
        const actions = document.createElement("div");
        actions.className = "item-actions";
        if (unread) {
          const markBtn = document.createElement("button");
          markBtn.type = "button";
          markBtn.className = "mark btn secondary sm";
          markBtn.textContent = L.markRead;
          const onMark = () => void markRead(item.deliveryId);
          markBtn.addEventListener("click", onMark);
          ctx.cleanup.push(() => markBtn.removeEventListener("click", onMark));
          actions.appendChild(markBtn);
        }
        const delBtn = document.createElement("button");
        delBtn.type = "button";
        delBtn.className = "del btn danger sm";
        delBtn.textContent = L.deleteOne;
        // Soft-delete im Postfach → Bestaetigung: die Flaeche bietet KEINE
        // Selbst-Ruecknahme (Restore ist admin-gated), also fuer den User
        // effektiv Einbahnstrasse (Kanon PFS-742).
        const onDel = (): void => {
          void confirmDialog({
            title: L.deleteOne,
            message: L.deleteConfirm,
            confirmLabel: L.deleteOne,
            cancelLabel: L.cancel,
            variant: "danger",
          }).then((ok) => {
            if (ok) void deleteOne(item.deliveryId);
          });
        };
        delBtn.addEventListener("click", onDel);
        ctx.cleanup.push(() => delBtn.removeEventListener("click", onDel));
        actions.appendChild(delBtn);
        li.appendChild(actions);
        return li;
      };

      let hasRendered = false;
      const run = async (): Promise<void> => {
        errorEl.hidden = true;
        if (!hasRendered) {
          loadingEl.hidden = false;
          listEl.hidden = true;
          emptyEl.hidden = true;
        }
        try {
          const body = await apiFetch<ListBody>(`${rawSource}?limit=${limit}`);
          loadingEl.hidden = true;
          const items = body.Items ?? [];
          listEl.replaceChildren(...items.map(paintItem));
          const empty = items.length === 0;
          emptyEl.hidden = !empty;
          listEl.hidden = empty;
          markAll.hidden = items.filter((i) => !i.readAt).length === 0;
          clearAll.hidden = items.length === 0;
          hasRendered = true;
        } catch {
          loadingEl.hidden = true;
          listEl.hidden = true;
          emptyEl.hidden = true;
          errorEl.hidden = false;
          errorEl.textContent = L.error;
        }
      };

      root.append(style, card);
      return run;
    }
  },
});
